import { isGranted as isGrantedBase } from '@pcg/auth';

import { useErrorStore } from '@/shared/stores/error';
import { useAuthStore } from '@/stores/auth';

import type { RouteLocationNormalized } from 'vue-router';
import type { ActionScopesArray } from '@pcg/auth';
import type { Permission } from '@dc/permissions';
import type { AuthorizeContext, AuthorizeFn } from '@/types/router-meta';

const isPublicPage = (route: RouteLocationNormalized): boolean => Boolean(route.meta.public);

/**
 * Runs the route's own `authorize` function against the same `@pcg/auth`
 * `isGranted` the API uses — `User.resolvedPermissions` ships the server's
 * decision table to the client, so the two cannot drift.
 *
 * A failure sets an inline 403 rather than redirecting: the user keeps the URL
 * they typed, and a permission granted a minute later makes a reload enough.
 * Must run after the auth middleware.
 *
 * @example Permission with no scope
 * ```ts
 * meta: { authorize: ({ isGranted }) => isGranted('dc:core:users:list', ['*']) }
 * ```
 *
 * @example With route params
 * ```ts
 * import * as s from '@pcg/auth/scopes';
 * meta: {
 *   authorize: ({ isGranted, route }) =>
 *     isGranted('dc:core:users:get', [s.id(route.params.id as string)]),
 * }
 * ```
 */
export const permissionMiddleware = (to: RouteLocationNormalized): void => {
  if (isPublicPage(to)) {
    return;
  }

  const authorize = to.meta.authorize as AuthorizeFn | undefined;

  if (!authorize) {
    return;
  }

  const auth = useAuthStore();
  const user = auth.user;

  if (!user) {
    return;
  }

  const isGranted = (permission: Permission, actionScopes?: ActionScopesArray): boolean =>
    isGrantedBase(user, permission, actionScopes);

  const ctx: AuthorizeContext = { isGranted, route: to };

  if (!authorize(ctx)) {
    useErrorStore().setError({
      statusCode: 403,
      title: 'Нет доступа',
      description: 'У тебя нет прав на этот раздел.',
    });
  }
};
